/**
 * Modifica dei soli dati di accompagnamento di un'uscita.
 *
 * NON c'è la composizione in big bag, e non è un'omissione: cambiarla
 * significherebbe riscrivere un FIR partito col camion. Chi ha sbagliato la
 * composizione annulla e ricrea — il numero di FIR torna libero apposta.
 *
 * Il cliente si blocca quando l'uscita è già fatturata: il backend risponde
 * 409 comunque, qui si evita il tentativo e si dice il perché.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import ClienteSearch from './ClienteSearch';
import { updateUscita, type UscitaDetail, type UscitaUpdate } from '../../api/uscite';
import type { Trasportatore } from '../../types/trasportatore';
import { apiErrorMessage } from '../../lib/apiError';

interface Props {
  uscita: UscitaDetail;
  trasportatori: Trasportatore[];
  onClose: () => void;
  onSaved: (u: UscitaDetail) => void;
}

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';

export default function ModificaUscitaModal({ uscita, trasportatori, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const fatturata = uscita.stato === 'fatturata' || uscita.stato === 'pagata';

  const [numeroFir, setNumeroFir] = useState(uscita.numero_fir);
  const [dataPartenza, setDataPartenza] = useState(uscita.data_partenza);
  const [cliente, setCliente] = useState<{ id: string; ragione_sociale: string } | null>(
    { id: uscita.customer_id, ragione_sociale: uscita.customer_ragione_sociale ?? '' },
  );
  const [trasportatoreId, setTrasportatoreId] = useState(uscita.trasportatore_id ?? '');
  const [pesoPonte, setPesoPonte] = useState(uscita.peso_ponte_kg != null ? String(uscita.peso_ponte_kg) : '');
  const [note, setNote] = useState(uscita.note ?? '');
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Chiusura implicita (ESC / click overlay) guardata mentre salva: `ui/Modal`
  // chiude su entrambi, cosa che un modale hand-rolled spesso non fa.
  const chiudi = () => { if (!busy) onClose(); };

  async function salva() {
    setBusy(true);
    setErrore(null);
    try {
      // `exclude_unset` lato backend: i nullable si mandano SEMPRE, anche a
      // null, altrimenti un trasportatore tolto resterebbe attaccato.
      const body: UscitaUpdate = {
        numero_fir: numeroFir.trim(),
        data_partenza: dataPartenza,
        trasportatore_id: trasportatoreId || null,
        peso_ponte_kg: pesoPonte ? Number(pesoPonte) : null,
        note: note.trim() || null,
      };
      if (!fatturata && cliente && cliente.id !== uscita.customer_id) body.customer_id = cliente.id;
      onSaved(await updateUscita(uscita.id, body));
    } catch (err: unknown) {
      setErrore(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('uscite.modifica.titolo')}
      subtitle={t('uscite.modifica.sottotitolo')}
      onClose={chiudi}
      testid="modifica-uscita-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy}
            className="px-3 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={() => void salva()} disabled={busy} data-testid="modifica-uscita-salva"
            className="px-3 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-50">
            {busy ? t('common.azioni.salvataggio_in_corso') : t('uscite.modifica.salva')}
          </button>
        </>
      )}
    >
      {errore && (
        <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
          {errore}
        </div>
      )}

      <div>
        <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.numero_fir')}</label>
        <input type="text" className={inputCls} value={numeroFir} data-testid="modifica-fir"
          onChange={(e) => setNumeroFir(e.target.value)} />
      </div>
      <div>
        <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.data_partenza')}</label>
        <input type="date" className={inputCls} value={dataPartenza}
          onChange={(e) => setDataPartenza(e.target.value)} />
      </div>
      <div>
        <ClienteSearch value={cliente} onChange={setCliente} label={t('uscite.wizard.cliente')}
          placeholder={t('uscite.wizard.cliente_placeholder')} disabled={fatturata} testid="modifica-cliente" />
        {fatturata && <p className="text-[11px] text-ink-faint mt-1">{t('uscite.modifica.cliente_bloccato')}</p>}
      </div>
      <div>
        <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.trasportatore')}</label>
        <select className={inputCls} value={trasportatoreId} onChange={(e) => setTrasportatoreId(e.target.value)}>
          <option value="">{t('uscite.wizard.trasportatore_nessuno')}</option>
          {trasportatori.map((tr) => <option key={tr.id} value={tr.id}>{tr.ragione_sociale}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.peso_ponte')}</label>
        <input type="number" step="0.01" className={inputCls} value={pesoPonte}
          onChange={(e) => setPesoPonte(e.target.value)} />
      </div>
      <div>
        <label className="block text-xs text-ink-soft mb-1">{t('uscite.wizard.note')}</label>
        <textarea className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}
